import { useMemo } from 'react'
import { isNil } from 'lodash'

import { formatCommentTime } from '@/features/view-tfl-uploaded/utils/timeFormat.ts'
import { useCurrentUser } from '@/hooks/useAuth.ts'
import { ResponseComment, ResponseThread } from '@/schema'

import { ECommentStatus } from '../../../constants'

export const useGetUserCommentData = (
  comment: ResponseComment | ResponseThread,
  threadStatus: string,
  now?: number
) => {
  const { data: currentUser } = useCurrentUser()

  const authorName = useMemo(() => {
    const { first_name, last_name } = comment.created_by_user ?? {}
    return `${first_name} ${last_name}`.trim()
  }, [comment.created_by_user])

  const isEdited = useMemo(
    () => !isNil(comment.content_last_edited_at),
    [comment.content_last_edited_at]
  )

  const avatarUrl = useMemo(
    () => comment.created_by_user?.avatar_url,
    [comment.created_by_user?.avatar_url]
  )

  const initials = useMemo(() => {
    const { first_name, last_name } = comment.created_by_user ?? {}
    return `${first_name?.[0] || ''}${last_name?.[0] || ''}`.toUpperCase()
  }, [comment.created_by_user])

  const isAuthor = useMemo(
    () => currentUser?.id === comment.created_by,
    [currentUser?.id, comment.created_by]
  )

  const relativeTime = useMemo(() => {
    if (isEdited && comment.content_last_edited_at) {
      return formatCommentTime(comment.content_last_edited_at)
    }

    return comment.created_at ? formatCommentTime(comment.created_at) : ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment.content_last_edited_at, comment.created_at, isEdited, now])

  const isDeletable = useMemo(
    () => isAuthor && !!threadStatus && threadStatus !== 'resolved',
    [threadStatus, isAuthor]
  )

  const isEditable = useMemo(
    () =>
      currentUser?.id === comment.created_by &&
      !!threadStatus &&
      threadStatus !== 'resolved',
    [comment.created_by, threadStatus, currentUser?.id]
  )

  const isQuotable = useMemo(
    () => !!threadStatus && threadStatus === ECommentStatus.OPEN,
    [threadStatus]
  )

  return {
    authorName,
    avatarUrl,
    initials,
    relativeTime,
    isAuthor,
    contentText: comment.content,
    isEdited,
    isEditable,
    isDeletable,
    isQuotable,
  }
}
